import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import cdrFilesService from '../../services/cdrFilesService';
import { Nav, Spinner } from 'react-bootstrap';
import { FaCogs, FaPrint, FaCut, FaBoxOpen, FaQuestionCircle, FaClock } from 'react-icons/fa';
import StatusBadge from './StatusBadge';
import FiltersBar from './FiltersBar';
import ViewModeSwitch from './ViewModeSwitch';
import PositionsTable from './PositionsTable';
import OrdersTable from './OrdersTable';
import SuppliesAccordion from './SuppliesAccordion';
import useRoles from '../../hooks/useRoles';

import MoveToLayoutingWarningModal from './MoveToLayoutingWarningModal';
import TransferSupplyWarningModal from './TransferSupplyWarningModal';
import ReLayoutWarningModal from './ReLayoutWarningModal';
import SupplyModal from './SupplyModal';
import StatusHistoryModal from './StatusHistoryModal';

const VIEW_MODES = {
  POSITIONS: 'positions',
  ORDERS: 'orders',
  SUPPLIES: 'supplies',
};

const OrdersList = () => {
  const { hasRole } = useRoles();
  const [orderPositions, setOrderPositions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [itemsHasCdrfiles, setItemsHasCdrfiles] = useState([]);
  const [filters, setFilters] = useState({
    namePart: '',
    sortParam: 'time_asc',
    categoryIds: [],
    materialIds: [],
    hasItem: null,
    hasCdrFile: null,
    pageSize: 60,
    pageNumber: 1,
  });

  const selectAllRef = useRef(null);

  const [activeTab, setActiveTab] = useState('wb'); // 'wb' | 'ozon'
  const [statusTab, setStatusTab] = useState('new'); // 'new' | 'packaging' | 'delivery'
  const [viewMode, setViewMode] = useState(VIEW_MODES.SUPPLIES);

  // выбранные позиции (универсально для всех режимов)
  const [selectedPositionIds, setSelectedPositionIds] = useState([]);

  // скачивание
  const [showMoveToLayoutingWarningModal, setMoveToLayoutingWarningModal] = useState(false);
  const [problematicOrderPositions, setProblematicOrderPositions] = useState([]);

  // модалка поставок
  const [showSupplyModal, setShowSupplyModal] = useState(false);
  // модалка предупреждения передачи на повторную компановку
  const [showReLayoutModal, setShowReLayoutModal] = useState(false);
  const [positionsToRelayout, setPositionsToRelayout] = useState([]);
  // модалка предупреждения перемещения из поставки
  const [showTransferSupplyModal, setShowTransferSupplyModal] = useState(false);
  const [positionsToTransfer, setPositionsToTransfer] = useState([]);
 // модалка истории изменения статусов
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyPosition, setHistoryPosition] = useState(null);

  const handleStatusClick = (position) => {
    setHistoryPosition(position);
    setShowHistoryModal(true);
  };

  // --- Загрузка справочников
  useEffect(() => {
    const fetchCategoriesAndMaterials = async () => {
      try {
        const [categoriesRes, materialsRes] = await Promise.all([
          api.get('/categories'),
          api.get('/materials'),
        ]);
        setCategories(categoriesRes.data || []);
        setMaterials(materialsRes.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCategoriesAndMaterials();
  }, []);

  // --- Загрузка позиций заказов
  const fetchOrderPositions = async () => {
    try {
      let endpoint = `/orders/${activeTab}/${statusTab}`;
      
      const res = await api.post(endpoint, filters);
      const orderPositions = (res.data && res.data.orderPositions) || [];
      setOrderPositions(orderPositions);
      setSelectedPositionIds([]);

      await fetchHasCdrfiles(orderPositions);
    } catch (err) {
      console.error('Ошибка при загрузке заказов:', err);
    }
  };

   // Функция для загрузки cdrfile по itemId
  const fetchHasCdrfiles = async (orderPositions) => {
    if (!orderPositions) return null;
    try {
      const itemIds = orderPositions.filter(op => op.itemId).map(op => op.itemId);
      const payload = { itemIds: itemIds};
      const response = await cdrFilesService.post(`/cdrfiles/has`, payload);
      
      var itemIdsHasCdrfiles = response.data;

      const itemIds_hasCdrfiles = {};
      for (const itemId of itemIdsHasCdrfiles) {
          itemIds_hasCdrfiles[itemId] = true;
      }
      setItemsHasCdrfiles(itemIds_hasCdrfiles);

    } catch (err) {
      console.error('Error loading image:', err);
      return null;
    }
  };

  // автообновление при смене вкладок или фильтров
  useEffect(() => {
    fetchOrderPositions();
  }, [filters, activeTab, statusTab]);
  
  // --- Группировки
  const groupedByOrder = useMemo(() => {
    const map = new Map(); // orderId -> { order, positions: [] }
    for (const p of orderPositions) {
      const orderId = p.order?.id || 'unknown';
      if (!map.has(orderId)) {
        map.set(orderId, { orderId, order: p.order ?? null, positions: [] });
      }
      map.get(orderId).positions.push(p);
    }
    return Array.from(map.values());
  }, [orderPositions]);

  const groupedBySupply = useMemo(() => {
    const map = new Map(); // supplyIdOrNull -> { supply, positions: [] }
    for (const p of orderPositions) {
      const supplyObj = p.supply ?? null;
      const supplyId = supplyObj?.id || 'NO_SUPPLY';
      if (!map.has(supplyId)) {
        map.set(supplyId, { supplyId, supply: supplyObj, positions: [] });
      }
      map.get(supplyId).positions.push(p);
    }
    // сортировка по времени поставки (новые выше), "без поставки" — в конец
    const arr = Array.from(map.values()).sort((a, b) => {
      if (a.supplyId === 'NO_SUPPLY') return 1;
      if (b.supplyId === 'NO_SUPPLY') return -1;
      const at = new Date(a.supply?.time || 0).getTime();
      const bt = new Date(b.supply?.time || 0).getTime();
      return bt - at;
    });
    return arr;
  }, [orderPositions]);

  // --- Счётчики и шапочный чекбокс
  const totalUnits = useMemo(() => {
    switch (viewMode) {
      case VIEW_MODES.ORDERS:
        return groupedByOrder.length;
      case VIEW_MODES.SUPPLIES:
        return groupedBySupply.length;
      case VIEW_MODES.POSITIONS:
      default:
        return orderPositions.length;
    }
  }, [viewMode, groupedByOrder.length, groupedBySupply.length, orderPositions.length]);

  const selectedUnits = useMemo(() => {
    switch (viewMode) {
      case VIEW_MODES.ORDERS:
        return groupedByOrder.filter(group =>
          group.positions.every(p => selectedPositionIds.includes(p.id))
        ).length;
      case VIEW_MODES.SUPPLIES:
        return groupedBySupply.filter(group =>
          group.positions.every(p => selectedPositionIds.includes(p.id))
        ).length;
      case VIEW_MODES.POSITIONS:
      default:
        return selectedPositionIds.filter(id =>
          orderPositions.some(p => p.id === id)
        ).length;
    }
  }, [viewMode, groupedByOrder, groupedBySupply, selectedPositionIds, orderPositions]);

  // проставляем indeterminate для шапочного чекбокса
  useEffect(() => {
    if (!selectAllRef.current) return;
    if (totalUnits === 0) {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = false;
      return;
    }
    const all = selectedUnits === totalUnits;
    const none = selectedUnits === 0;
    selectAllRef.current.indeterminate = !all && !none;
    selectAllRef.current.checked = all;
  }, [selectedUnits, totalUnits]);

  // --- Handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, pageNumber: 1 }));
  };

  const handleTriStateToggle = (field, currentValue) => {
    const next = currentValue === null ? true : currentValue === true ? false : null;
    setFilters((prev) => ({ ...prev, [field]: next, pageNumber: 1 }));
  };

  // выделить всё в зависимости от текущего режима
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    if (!checked) {
      setSelectedPositionIds([]);
      return;
    }

    if (viewMode === VIEW_MODES.POSITIONS) {
      const allIds = orderPositions.map(p => p.id);
      setSelectedPositionIds(allIds);
    } else if (viewMode === VIEW_MODES.ORDERS) {
      const allIds = groupedByOrder.flatMap(g => g.positions.map(p => p.id));
      setSelectedPositionIds(allIds);
    } else if (viewMode === VIEW_MODES.SUPPLIES) {
      const allIds = groupedBySupply.flatMap(g => g.positions.map(p => p.id));
      setSelectedPositionIds(allIds);
    }
  };

  const togglePosition = (position) => {
    const id = position.id;
    setSelectedPositionIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleOrderGroup = (group) => {
    const ids = group.positions.map(p => p.id);
    const allSelected = ids.every(id => selectedPositionIds.includes(id));
    setSelectedPositionIds(prev => {
      if (allSelected) return prev.filter(id => !ids.includes(id));
      const add = ids.filter(id => !prev.includes(id));
      return [...prev, ...add];
    });
  };

  const toggleSupplyGroup = (group) => {
    const ids = group.positions.map(p => p.id);
    const allSelected = ids.every(id => selectedPositionIds.includes(id));
    setSelectedPositionIds(prev => {
      if (allSelected) return prev.filter(id => !ids.includes(id));
      const add = ids.filter(id => !prev.includes(id));
      return [...prev, ...add];
    });
  };

  // --- Download logic
  const findProblematic = (selectedIds) => {
    return orderPositions.filter(
      (o) => selectedIds.includes(o.id) && !itemsHasCdrfiles[o.itemId]
    );
  };

  const handleOpenSupplyAction = () => {
    const selected = orderPositions.filter(p =>
      selectedPositionIds.includes(p.id)
    );

    const inSupply = selected.filter(p => p.supply !== null);

    if (inSupply.length > 0) {
      setPositionsToTransfer(inSupply);
      setShowTransferSupplyModal(true);
      return;
    }

    setShowSupplyModal(true);
  };

  // --- Передача на компоновку
  const handleMoveToLayouting = () => {
    if (selectedPositionIds.length === 0) return;

    const selected = orderPositions.filter(p =>
      selectedPositionIds.includes(p.id)
    );

    const problematic = findProblematic(selectedPositionIds);
    if (problematic.length > 0) {
      setProblematicOrderPositions(problematic);
      setMoveToLayoutingWarningModal(true);
      return;
    }

    // NEW: проверка статуса
    const nonNew = selected.filter(
      p => p.status !== 'new' && p.status !== 'unknown'
    );

    if (nonNew.length > 0) {
      setPositionsToRelayout(nonNew);
      setShowReLayoutModal(true);
      return;
    }

    // иначе обычная передача
    moveToLayouting(selectedPositionIds);
  };


  const moveToLayouting = async (finalSelected) => {
    try {
      const orderPositionIds = orderPositions
        .filter((o) => finalSelected.includes(o.id))
        .map((o) => o.orderPositionId);

      await api.patch('/orders/move-to-layouting', {
        orderPositionIds,
      });

      // Убираем эти позиции из списка — считаем, что они переданы
      setOrderPositions(prev => prev.filter(o => !finalSelected.includes(o.id)));
      
      // обновляем список после успешной передачи
      await fetchOrderPositions();

      // сбрасываем выделение
      setSelectedPositionIds([]);
    } catch (err) {
      console.error('Ошибка при передаче на компоновку:', err);
    }
  };

  const canTakeFromWarehouse = useMemo(() => {
    if (selectedPositionIds.length === 0) return false;

    const selected = orderPositions.filter(p => selectedPositionIds.includes(p.id));

    // 1) если есть хоть одна позиция warehouseCount == 0 → нельзя
    if (selected.some(p => p.warehouseCount === 0)) return false;

    // 2) группируем по itemId
    const grouped = selected.reduce((acc, p) => {
      if (!p.itemId) return acc;
      acc[p.itemId] = acc[p.itemId] || { count: 0, warehouse: p.warehouseCount };
      acc[p.itemId].count += 1;
      acc[p.itemId].warehouse = p.warehouseCount;
      return acc;
    }, {});

    // 3) если берём больше, чем на складе → нельзя
    return Object.values(grouped).every(g => g.count <= g.warehouse);
  }, [selectedPositionIds, orderPositions]);

  const handleTakeFromWarehouse = async () => {
    try {

      const orderPositionIds = orderPositions
        .filter((o) => selectedPositionIds.includes(o.id))
        .map((o) => o.orderPositionId);

      await api.patch('/orders/take-from-warehouse', {
        orderPositionIds: orderPositionIds
      });

      // после успешного списания — обновить список
      await fetchOrderPositions();
      setSelectedPositionIds([]);

    } catch (err) {
      console.error(err);
      alert("Количество товаров на складе обновилось — пожалуйста, перезагрузите страницу.");
    }
  };


  // --- Рендер-хелперы
  const headerCountLabel = `(${selectedUnits}/${totalUnits})`;

  const renderRowPosition = (p, index) => {
    const id = p.id;
    const createdAt = new Date(p.createdAt);
    const orderExternalId = p.order?.externalId;

    return (
      <tr key={`${id}-${index}`}>
        <td>
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedPositionIds.includes(id)}
            onChange={() => togglePosition(p)}
          />
        </td>
        <td>
          {orderExternalId}
          <br />
          <small>{id}</small>
          <br />
          <small>{createdAt.toLocaleString()}</small>
          <div className="mt-2" style={{ cursor: 'pointer' }} onClick={() => handleStatusClick(p)}>
            <StatusBadge status={p.status} />
          </div>
          {p.warehouseCount > 0 && (
            <div className="text-success">
              Есть на складе: {p.warehouseCount} шт.
            </div>
          )}
        </td>
        <td>
          <img
            src={p.image}
            alt={p.article}
            style={{ width: '100px', height: '100px', objectFit: 'contain' }}
          />
        </td>
        <td>
          <div>{p.name}</div>
          <div>{p.article}</div>
          {!p.hasItem && <div className="text-danger">Товар не найден</div>}
          {!itemsHasCdrfiles[p.itemId] && <div className="text-danger">Макет не найден</div>}
        </td>
        <td>{((p.convertedPrice || 0) / 100).toFixed(2)} ₽</td>
      </tr>
    );
  };

  return (
    <div className="container mt-4">
      {/* Marketplace tabs */}
      <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={(key) => {
          setActiveTab(key);
          setOrderPositions([]);
          setSelectedPositionIds([]);
        }}
      >
        <Nav.Item>
          <Nav.Link eventKey="wb">WB</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="ozon">Ozon</Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="d-flex align-items-center justify-content-between mt-3">
        <h1 className="m-0">
          {activeTab === 'wb' ? 'Заказы Wildberries' : 'Заказы Ozon'}
        </h1>

        <ViewModeSwitch
          value={viewMode}
          onChange={(v) => {
            setViewMode(v);
            setSelectedPositionIds([]);
          }}
        />
      </div>

      {/* Фильтры */}
      <FiltersBar
        filters={filters}
        categories={categories}
        materials={materials}
        onChangeInput={handleFilterChange}
        onToggleTri={(field, value) => handleTriStateToggle(field, value)}
        setFilters={setFilters}
      />

      {/* Кнопки действий */}
      <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
        <button
          className="btn btn-primary"
          onClick={handleOpenSupplyAction}
          disabled={selectedPositionIds.length === 0}
        >
          {statusTab === 'new' ? 'Перевести на сборку' : 'Добавить к поставке'}
        </button>
        
        <div>
          {hasRole('MoveOrdersToLayouting') && statusTab != 'new' && (
            <button
              className="btn btn-warning me-2"
              onClick={handleMoveToLayouting}
              disabled={selectedPositionIds.length === 0}
            >
              Передать на компоновку
            </button>
          )}

          {hasRole('TakeItemsFromWarehouse') && statusTab != 'new' && (
            <button
              className="btn btn-success"
              disabled={!canTakeFromWarehouse}
              onClick={handleTakeFromWarehouse}
            >
              Взять со склада
            </button>
          )}
        </div>
        
      </div>

      {/* Вкладки статуса */}
      <Nav
        variant="tabs"
        activeKey={statusTab}
        onSelect={(key) => {
          setStatusTab(key);
          setOrderPositions([]);
          setSelectedPositionIds([]);
        }}
      >
        <Nav.Item>
          <Nav.Link eventKey="new">Новые</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="packaging">На сборке</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="delivery">В доставке</Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Контент */}
      <div className="mt-3">
        {viewMode === VIEW_MODES.POSITIONS && (
          <PositionsTable
            selectAllRef={selectAllRef}
            headerCountLabel={headerCountLabel}
            onSelectAll={handleSelectAll}
            orderPositions={orderPositions}
            selectedPositionIds={selectedPositionIds}
            onTogglePosition={togglePosition}
            renderRowPosition={renderRowPosition}
          />
        )}

        {viewMode === VIEW_MODES.ORDERS && (
          <OrdersTable
            selectAllRef={selectAllRef}
            headerCountLabel={headerCountLabel}
            onSelectAll={handleSelectAll}
            groupedByOrder={groupedByOrder}
            selectedPositionIds={selectedPositionIds}
            onToggleOrderGroup={toggleOrderGroup}
          />
        )}

        {viewMode === VIEW_MODES.SUPPLIES && (
          <SuppliesAccordion
            selectAllRef={selectAllRef}
            headerCountLabel={headerCountLabel}
            onSelectAll={handleSelectAll}
            groupedBySupply={groupedBySupply}
            selectedPositionIds={selectedPositionIds}
            onToggleSupplyGroup={toggleSupplyGroup}
            renderRowPosition={renderRowPosition}
          />
        )}
      </div>

      {/* Модал предупреждения о товарах и мактах */}
      <MoveToLayoutingWarningModal
        show={showMoveToLayoutingWarningModal}
        onClose={() => setMoveToLayoutingWarningModal(false)}
        problematicOrderPositions={problematicOrderPositions}
        selectedPositionIds={selectedPositionIds}
        setSelectedPositionIds={setSelectedPositionIds}
        onConfirm={(ids) => moveToLayouting(ids)} // 👈 теперь вызывается метод передачи на компоновку
      />

      {/* Модал предупреждения повторной компановки */}
      <ReLayoutWarningModal
        show={showReLayoutModal}
        onClose={() => setShowReLayoutModal(false)}
        positions={positionsToRelayout}
        onConfirm={() => {
          setShowReLayoutModal(false);
          moveToLayouting(selectedPositionIds);
        }}
      />

      {/* Модал предупреждения о переносе из поставки */}
      <TransferSupplyWarningModal
        show={showTransferSupplyModal}
        positions={positionsToTransfer}
        onClose={() => setShowTransferSupplyModal(false)}
        onConfirm={() => {
          setShowTransferSupplyModal(false);
          setShowSupplyModal(true);
        }}
      />

      {/* Модал поставок — 2 таба (существующие / создать новую) и новые эндпоинты */}
      <SupplyModal
        show={showSupplyModal}
        onHide={() => setShowSupplyModal(false)}
        service={activeTab} // 'wb' | 'ozon'
        orderPositions={orderPositions}
        selectedPositionIds={selectedPositionIds}
        afterSuccess={() => setShowSupplyModal(false)}
      />

      <StatusHistoryModal
        show={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        position={historyPosition}
      />

    </div>
  );
};

export default OrdersList;
